import { useMemo, useState } from "react";
import { erWaitTimes } from "../Data/erWaitTimes";

const formatWaitTime = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr`;
  return `${hrs} hr ${mins} min`;
};

const getStatusStyle = (minutes) => {
  if (minutes <= 60) return "bg-green-100 text-green-800 border-green-300";
  if (minutes <= 180) return "bg-yellow-100 text-yellow-800 border-yellow-300";
  if (minutes <= 300) return "bg-orange-100 text-orange-800 border-orange-300";
  return "bg-red-100 text-red-800 border-red-300";
};

const getCapacityColor = (capacity) => {
  if (capacity <= 40) return "bg-green-500";
  if (capacity <= 70) return "bg-yellow-500";
  if (capacity <= 90) return "bg-orange-500";
  return "bg-red-500";
};

export default function ERWaitTimes() {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("wait");
  const [selectedHospital, setSelectedHospital] = useState(null);

  const hospitals = useMemo(() => {
    let result = erWaitTimes.filter((hospital) => {
      const searchText = `${hospital.hospitalName} ${hospital.city} ${hospital.address}`.toLowerCase();
      return searchText.includes(search.toLowerCase());
    });

    if (sortBy === "wait") {
      result = [...result].sort((a, b) => a.waitTimeMinutes - b.waitTimeMinutes);
    }

    if (sortBy === "distance") {
      result = [...result].sort((a, b) => a.distanceKm - b.distanceKm);
    }

    if (sortBy === "capacity") {
      result = [...result].sort((a, b) => a.capacityPercent - b.capacityPercent);
    }

    return result;
  }, [search, sortBy]);

  const totalWaiting = erWaitTimes.reduce((sum, h) => sum + h.patientsWaiting, 0);
  const totalTreated = erWaitTimes.reduce((sum, h) => sum + h.patientsBeingTreated, 0);
  const totalDoctors = erWaitTimes.reduce((sum, h) => sum + h.erDoctors, 0);
  const totalNurses = erWaitTimes.reduce((sum, h) => sum + h.nurses, 0);

  const avgWait = Math.round(
    erWaitTimes.reduce((sum, h) => sum + h.waitTimeMinutes, 0) / erWaitTimes.length
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-r from-blue-800 via-blue-700 to-cyan-600 text-white rounded-3xl p-6 shadow-xl mb-6">
          <p className="text-sm uppercase tracking-wide text-blue-100">
            PinMyDoc Emergency Care
          </p>

          <h1 className="text-3xl md:text-4xl font-bold mt-2">
            Live ER Wait Times & Hospital Status
          </h1>

          <p className="mt-3 text-blue-50 max-w-3xl">
            Check estimated ER wait times, patients waiting, hospital capacity,
            staff availability, and emergency care status near you.
          </p>

          <div className="mt-5 bg-red-500/20 border border-red-200/50 rounded-2xl p-4 text-sm">
            <strong>Emergency Notice:</strong> ER wait times are estimates only.
            Patients are treated based on medical urgency, not arrival order.
            For life-threatening emergencies, call 911 immediately.
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <SummaryCard label="Hospitals Tracking" value={erWaitTimes.length} />
          <SummaryCard label="Patients Waiting" value={totalWaiting} />
          <SummaryCard label="Being Treated" value={totalTreated} />
          <SummaryCard label="ER Doctors" value={totalDoctors} />
          <SummaryCard label="ER Nurses" value={totalNurses} />
          <SummaryCard label="Avg Wait" value={formatWaitTime(avgWait)} />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search hospital, city, or address..."
            className="flex-1 border border-slate-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500"
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="wait">Shortest wait time</option>
            <option value="distance">Closest hospital</option>
            <option value="capacity">Lowest capacity</option>
          </select>
        </div>

        <div className="grid gap-5">
          {hospitals.map((hospital) => (
            <div
              key={hospital.id}
              className="bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition p-5"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    {hospital.hospitalName}
                  </h2>
                  <p className="text-slate-600 mt-1">{hospital.address}</p>
                </div>

                <div
                  className={`rounded-2xl border px-5 py-4 text-center ${getStatusStyle(
                    hospital.waitTimeMinutes
                  )}`}
                >
                  <p className="text-xs font-semibold uppercase">
                    Estimated Wait
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {formatWaitTime(hospital.waitTimeMinutes)}
                  </p>
                  <p className="text-sm mt-1">{hospital.crowdLevel}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                <Metric label="Patients Waiting" value={hospital.patientsWaiting} />
                <Metric label="Being Treated" value={hospital.patientsBeingTreated} />
                <Metric label="Doctors" value={hospital.erDoctors} />
                <Metric label="Nurses" value={hospital.nurses} />
              </div>

              <div className="mt-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-slate-700">ER Capacity</span>
                  <span className="font-bold text-slate-900">
                    {hospital.capacityPercent}%
                  </span>
                </div>

                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getCapacityColor(hospital.capacityPercent)}`}
                    style={{ width: `${hospital.capacityPercent}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Badge>{hospital.status}</Badge>
                <Badge>{hospital.distanceKm} km away</Badge>
                <Badge>Updated {hospital.lastUpdated}</Badge>
                <Badge>Confidence {hospital.confidenceScore}%</Badge>
                {hospital.hasPediatricER ? <Badge>Pediatric ER</Badge> : null}
                {hospital.hasVirtualTriage ? <Badge>Virtual Triage</Badge> : null}
              </div>

              <div className="mt-5 border-t border-slate-100 pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <p className="text-sm text-slate-500">
                  Patients seen today: {hospital.patientsSeenToday} • Discharged:{" "}
                  {hospital.patientsDischargedToday} • Ambulances incoming:{" "}
                  {hospital.ambulancesIncoming}
                </p>

                <div className="flex flex-wrap gap-2">
                  <a
                    href={`tel:${hospital.phone}`}
                    className="px-4 py-2 rounded-xl bg-slate-100 text-slate-800 text-sm font-medium hover:bg-slate-200"
                  >
                    Call
                  </a>

                  <a
                    href={
                      "https://www.google.com/maps/search/?api=1&query=" +
                      encodeURIComponent(hospital.address)
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Directions
                  </a>

                  <button
                    type="button"
                    onClick={() => setSelectedHospital(hospital)}
                    className="px-4 py-2 rounded-xl bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedHospital ? (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {selectedHospital.hospitalName}
                  </h2>
                  <p className="text-slate-600 mt-1">
                    {selectedHospital.address}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedHospital(null)}
                  className="rounded-full bg-slate-100 px-4 py-2 text-slate-700 hover:bg-slate-200"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <Metric
                  label="Current Wait"
                  value={formatWaitTime(selectedHospital.waitTimeMinutes)}
                />
                <Metric label="Waiting" value={selectedHospital.patientsWaiting} />
                <Metric
                  label="Being Treated"
                  value={selectedHospital.patientsBeingTreated}
                />
                <Metric label="Beds Available" value={selectedHospital.bedsAvailable} />
              </div>

              <div className="mt-6 bg-slate-50 rounded-2xl p-5">
                <h3 className="font-bold text-slate-900 mb-4">
                  Patient Flow Timeline
                </h3>

                <TimelineItem
                  label="Registration"
                  value={`${selectedHospital.timeline.registration} min`}
                />
                <TimelineItem
                  label="Triage"
                  value={`${selectedHospital.timeline.triage} min`}
                />
                <TimelineItem
                  label="Waiting Room"
                  value={`${selectedHospital.timeline.waitingRoom} min`}
                />
                <TimelineItem
                  label="Doctor Assessment"
                  value={`${selectedHospital.timeline.doctorAssessment} min`}
                />
              </div>

              <div className="mt-6 bg-slate-50 rounded-2xl p-5">
                <h3 className="font-bold text-slate-900 mb-4">
                  Wait Time Trend Today
                </h3>

                <div className="grid grid-cols-5 gap-2 items-end h-40">
                  {selectedHospital.trend.map((item) => (
                    <div
                      key={item.time}
                      className="flex flex-col items-center justify-end h-full"
                    >
                      <div
                        className="w-full rounded-t-xl bg-blue-500"
                        style={{ height: `${Math.max(item.wait / 3, 20)}px` }}
                      />
                      <p className="text-xs mt-2 text-slate-500">{item.time}</p>
                      <p className="text-xs font-semibold">
                        {formatWaitTime(item.wait)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-5">
                <h3 className="font-bold text-blue-900">PinMyDoc Guidance</h3>
                <p className="text-sm text-blue-800 mt-2">
                  This ER is currently marked as{" "}
                  <strong>{selectedHospital.crowdLevel}</strong>. If your condition is
                  not urgent, consider calling the hospital or checking nearby
                  walk-in or urgent care options. This is informational only and not
                  medical advice.
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
      {children}
    </span>
  );
}

function TimelineItem({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 py-3 last:border-b-0">
      <span className="text-slate-600">{label}</span>
      <span className="font-bold text-slate-900">{value}</span>
    </div>
  );
}